/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/forbid-prop-types */
import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import useForm from '@base/Form/useForm';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useRouter } from 'next/router';

import fetch from '@utils/api-helper/fetch';
import {
  setLocalEmailAndName,
  setLocalPhoneNumber,
  sendOtpOrEmail
} from '@actions/auth';

import {
  DEFAULT_COUNTRY,
  OFFLINE_GOAL_UID
} from '@cont/OfflineCentres/constants';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { useSnackbar } from '@stores/snackbar';
import { StyledTermsSpan } from '@cont/OfflineCentres/PostPaymentLearnerDetails/styles';
import Divider from '@base/Divider/Divider';
import H3 from '@base/H3/H3';
import {
  UNSAT_Page_Landed,
  UNSAT_Registration_PI_Details,
  UNSAT_Registration_Submitted
} from '@constants/segment';
import { segment } from '@actions/analytics';
import { getSubmitFormURL } from '@cont/OfflineCentres/PostPaymentLearnerDetails/apiHelper';
import debounce from 'lodash/debounce';
import {
  FormContainer,
  BookClassButon,
  AcceptTerms,
  TermsContent,
  TermsLink,
  StyP2,
  ClassTitle,
  BookAClassForm,
  SubmitWrapper,
  PriceWrapper,
  PriceP2,
  PostPaymentFormContainer
} from './styles';
import { StyledPaper } from '../BookScholarshipSchool/styles';

import PersonalDetails from './PersonalDetails';
import TestDetails from './TestDetails';

const errorMessage = 'Something went wrong, Try again later';

const BookScholarshipForm = ({
  heroForm,
  dateSlots,
  cityCenterMapping,
  formDetails,
  setIsRedirection,
  isRedirecting,
  isOnlineSelected,
  setIsOnlineSelected
}) => {
  const {
    redirectionUrl,
    fields,
    header: { title },
    formPrice
  } = heroForm;
  const { getState } = useStore();
  const dispatch = useDispatch();
  const router = useRouter();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);

  const { query } = useRouter();

  useEffect(() => {
    setIsOnlineSelected(formDetails.testType.value === 'online');
  }, [formDetails]);

  const [payload, setPayload] = useState(null);
  const { open: showSnackbar } = useSnackbar();

  const { registrar, onFormSubmit } = useForm({
    data: formDetails
  });
  const [isSubmitting, setIsSubmiiting] = useState(false);

  useEffect(() => {
    segment.track(UNSAT_Page_Landed, {
      form_type: 'non_school'
    });
  }, []);

  const updateFormSubmitted = debounce(() => {
    setIsSubmiiting(false);
  }, 1000);

  const setUnsatData = useCallback(() => {
    const body = {
      'form-type': 3,
      'form-details': { ...payload }
    };
    setIsSubmiiting(true);
    fetch(getSubmitFormURL(), {
      getState,
      requestType: 'POST',
      body
    })
      .then(async (res) => {
        if (res.error || res.error_code) {
          const errorResponse = await res.error.response.json();
          showSnackbar({
            message:
              errorResponse?.message ||
              'Something went wrong. Please try again.',
            type: 'error'
          });
        } else {
          setIsRedirection(true);
          segment.track(UNSAT_Registration_Submitted, {
            unsat_exam_class: payload?.examAndClass,
            test_mode: payload.testType,
            city_name: payload.city,
            centre: payload.centerName,
            exam_slot: payload.dateAndTime,
            form_type: 'non_school',
            goal_uid: OFFLINE_GOAL_UID
          });
          const { referral_code } = query;
          if (referral_code)
            router.push({
              pathname: redirectionUrl,
              query: {
                referral_code
              }
            });
          else router.push(redirectionUrl);
        }
      })
      .catch(() => {
        showSnackbar({
          type: 'SET_SNACKBAR_MESSAGE',
          message: errorMessage,
          messageType: 'error'
        });
      })
      .finally(updateFormSubmitted());
  }, [
    showSnackbar,
    getState,
    payload,
    redirectionUrl,
    router,
    setIsRedirection
  ]);

  useEffect(() => {
    if (isLoggedIn && payload && !isRedirecting) {
      setUnsatData();
    }
  }, [isLoggedIn, payload, isRedirecting]);

  const onSubmit = async (data) => {
    setPayload({
      fullName: data.fullName,
      email: data.email,
      phoneNum: data.phoneNum,
      examAndClass: data.examAndClass?.value,
      testType: data.testType?.value,
      city: data.city?.value,
      centerName: data.centerName?.value,
      dateAndTime: data.dateAndTime?.value,
      isFormSubmitted: true
    });

    if (!isLoggedIn) {
      dispatch(
        sendOtpOrEmail(String(data.phoneNum), DEFAULT_COUNTRY.code, 1)
      ).then((response = {}) => {
        if (!response?.result?.is_registered) {
          dispatch(
            setLocalEmailAndName(String(data.email), String(data.fullName))
          );
        }
      });
      dispatch(setLocalPhoneNumber(String(data.phoneNum), DEFAULT_COUNTRY));
      openLoginDrawer();
    }
    segment.track(UNSAT_Registration_PI_Details, {
      unsat_exam_class: data?.examAndClass.value,
      test_mode: data.testType.value,
      city_name: data.city.value,
      centre: data.centerName.value,
      exam_slot: data.dateAndTime.value,
      form_type: 'non_school',
      phone_number: data.phoneNum,
      email_id: data.email
    });
  };

  const handleOnSubmit = onFormSubmit(onSubmit);

  return (
    <>
      <PostPaymentFormContainer>
        <StyledPaper>
          <BookAClassForm $display>
            <ClassTitle>{title}</ClassTitle>
            <FormContainer>
              <PersonalDetails
                registrar={registrar}
                country={DEFAULT_COUNTRY}
              />
              <TestDetails
                registrar={registrar}
                fields={fields}
                dateSlots={dateSlots}
                cityCenterMapping={cityCenterMapping}
                isOnlineSelected={isOnlineSelected}
                setIsOnlineSelected={setIsOnlineSelected}
              />
            </FormContainer>
            <SubmitWrapper>
              {formPrice ? (
                <PriceWrapper>
                  <PriceP2>Price</PriceP2>
                  <H3>{formPrice}</H3>
                </PriceWrapper>
              ) : null}
              <BookClassButon
                onClick={handleOnSubmit}
                width="auto"
                variant={isSubmitting ? 'disabled' : 'filled'}
                label="Continue to payment"
                color="black"
              />
            </SubmitWrapper>
            <Divider />
            <AcceptTerms>
              <TermsContent>
                <StyP2>By continuing, you agree to</StyP2>
                <TermsLink target="_blank" href="/unsat-terms">
                  <StyledTermsSpan> Unacademy’s terms</StyledTermsSpan>
                </TermsLink>
              </TermsContent>
            </AcceptTerms>
          </BookAClassForm>
        </StyledPaper>
      </PostPaymentFormContainer>
    </>
  );
};

BookScholarshipForm.propTypes = {
  heroForm: PropTypes.objectOf(PropTypes.any).isRequired
};

export default BookScholarshipForm;
